import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stack,
  Typography,
  Button,
  Paper,
  ButtonGroup,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { useGet } from "../../hooks/api";
import { apiUrls } from "../../utils/urls";
import api from "../../utils/api";
import { ClockIcon } from "@mui/x-date-pickers-pro";
import useDisclosure from "../../hooks/ui/useDisclosure";
import SubmitHoursDialog from "./SubmitHoursDialog";
import dayjs from "dayjs";
import { Event as EventIcon } from "@mui/icons-material";
import { Box } from "@mui/system";
import RecentTimeEvents from "../RecentTimeEvents/RecentTimeEvents";

function pad(num) {
  return num.toString().padStart(2, "0");
}

function formatDuration(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export default function TimeTracker({
  includeSubmitHoursButton = true,
  includeHoursSubmittedToday = true,
  includeClockEvents = false,
  readOnly = false,
  clockEventsEndpoint = apiUrls.clock.events(),
  clockInStatusEndpoint = apiUrls.clock.status(),
  timeSubmissionEndpoint = apiUrls.clock.timeSubmission(),
  clockInEndpoint = apiUrls.clock.in(),
  clockOutEndpoint = apiUrls.clock.out(),
}) {
  // Fetch current clock-in status
  const { data: clockInStatusData, refetch: refetchClockStatus } = useGet(
    clockInStatusEndpoint,
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  const {
    data: todaysTimeSubmissionData,
    refetch: refetchTodaysTimeSubmission,
  } = useGet(timeSubmissionEndpoint, {
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    params: {
      search_params: {
        date: dayjs().format("YYYY-MM-DD"),
      },
    },
    enabled: includeHoursSubmittedToday,
    staleTime: 0,
  });
  const clockEventsDisclosure = useDisclosure();

  const today = dayjs();
  const currentDay = today.date();

  let minDate;

  if (currentDay <= 10) {
    const prevMonth = today.subtract(1, "month");
    minDate = prevMonth.startOf("month");
  } else {
    minDate = today.startOf("month");
  }
  const { data: prevSubmissionData, refetch: refetchPrevSubmissions } = useGet(
    timeSubmissionEndpoint,
    {
      params: {
        search_params: {
          date__gte: minDate.format("YYYY-MM-DD"),
          date__lt: dayjs().format("YYYY-MM-DD"),
        },
        values: ["date"],
      },
      enabled: includeSubmitHoursButton,
    }
  );

  const prevDays = dayjs().diff(minDate, "days");
  const daysSubmittedSet = useMemo(
    () => new Set((prevSubmissionData?.data || []).map(({ date }) => date)),
    [prevSubmissionData]
  );
  const daysNotSubmitted = prevDays - (daysSubmittedSet.size ?? 0);

  const submitHoursDisc = useDisclosure();

  const isClockedIn = clockInStatusData?.data.clocked_in;
  const clockInDt = clockInStatusData?.data.clock_in_dt; // ISO string or null

  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Compute initial elapsed time if already clocked in
  useEffect(() => {
    if (isClockedIn && clockInDt) {
      const startMs = new Date(clockInDt).getTime();
      const nowMs = Date.now();
      const diffSeconds = Math.floor((nowMs - startMs) / 1000);
      setElapsedSeconds(diffSeconds);
    } else {
      setElapsedSeconds(0);
    }
  }, [isClockedIn, clockInDt]);

  // When clocked in, increment elapsedSeconds every second
  useEffect(() => {
    let timerId;
    if (isClockedIn) {
      timerId = setInterval(() => {
        setElapsedSeconds(dayjs().diff(dayjs(clockInDt), "seconds"));
      }, 1000);
    }
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [isClockedIn, clockInDt]);

  // Handle Clock In button press
  const handleClockIn = useCallback(async () => {
    if (readOnly) {
      return;
    }
    const response = await api.post(clockInEndpoint);
    if (response.status === 200) {
      refetchClockStatus();
    }
  }, [readOnly, refetchClockStatus, clockInEndpoint]);

  const handleClockOut = useCallback(async () => {
    if (readOnly) {
      return;
    }
    const response = await api.post(clockOutEndpoint);
    if (response.status === 200) {
      refetchClockStatus();
    }
  }, [readOnly, refetchClockStatus, clockOutEndpoint]);

  const hoursWorkedToday =
    todaysTimeSubmissionData?.data[0]?.hours_worked ?? null;

  return (
    <>
      {includeClockEvents && (
        <Dialog
          {...clockEventsDisclosure.getDisclosureProps()}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Clock Events</DialogTitle>
          <DialogContent>
            <Box sx={{ height: "400px" }}>
              <RecentTimeEvents clockEventsEndpoint={clockEventsEndpoint} />
            </Box>
          </DialogContent>
        </Dialog>
      )}
      <SubmitHoursDialog
        {...submitHoursDisc.getDisclosureProps()}
        onSubmit={() => {
          refetchPrevSubmissions();
          refetchTodaysTimeSubmission();
          submitHoursDisc.onClose();
        }}
        hoursSubmissionEndpoint={timeSubmissionEndpoint}
        clockEventsEndpoint={clockEventsEndpoint}
      />
      <Stack spacing={1}>
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Stack spacing={1} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon color={isClockedIn ? "success" : "disabled"} />
              <Typography
                variant="subtitle1"
                color={isClockedIn ? "textPrimary" : "textSecondary"}
              >
                {isClockedIn ? "Clocked In" : "Clocked Out"}
              </Typography>
            </Stack>

            {isClockedIn && (
              <Typography variant="h5">
                {formatDuration(elapsedSeconds)}
              </Typography>
            )}
            <ButtonGroup size="small">
              <Button
                color={isClockedIn ? "secondary" : "primary"}
                startIcon={isClockedIn ? <StopIcon /> : <PlayArrowIcon />}
                onClick={isClockedIn ? handleClockOut : handleClockIn}
                disabled={readOnly}
              >
                {isClockedIn ? "Clock Out" : "Clock In"}
              </Button>
              {includeSubmitHoursButton && (
                <Badge
                  variant="standard"
                  badgeContent={clockInStatusData ? daysNotSubmitted : 0}
                  overlap="circular"
                  color="secondary"
                  sx={{
                    "& .MuiBadge-badge": {
                      right: 0,
                      top: 0,
                    },
                  }}
                >
                  <Button
                    startIcon={<ClockIcon />}
                    disabled={readOnly || isClockedIn}
                    {...submitHoursDisc.getButtonProps()}
                  >
                    Submit Hours
                  </Button>
                </Badge>
              )}
              {includeClockEvents && (
                <Button
                  startIcon={<EventIcon />}
                  onClick={clockEventsDisclosure.onOpen}
                  disabled={readOnly}
                >
                  Clock Events
                </Button>
              )}
            </ButtonGroup>
            {includeHoursSubmittedToday && hoursWorkedToday && (
              <Typography variant="body2" color="success">
                Hours submitted today:{" "}
                <span style={{ fontWeight: "bold" }}>{hoursWorkedToday}</span>
              </Typography>
            )}
          </Stack>
        </Paper>
      </Stack>
    </>
  );
}
